import {
  PaginationProps,
  PropertyFilterProps,
  TableProps,
} from "@amzn/awsui-components-react/polaris";
import { Task } from "models/schemas";
import moment from "moment";
import {
  formatDateValue,
  toDateString,
  toEndOfDayTimestamp,
} from "./date-format";

export const commonPropertyFilterI18nStrings: PropertyFilterProps.I18nStrings =
  {
    filteringAriaLabel: "your choice",
    dismissAriaLabel: "Dismiss",

    groupValuesText: "Values",
    groupPropertiesText: "Properties",
    operatorsText: "Operators",

    operationAndText: "and",
    operationOrText: "or",

    operatorLessText: "Less than",
    operatorLessOrEqualText: "Less than or equal",
    operatorGreaterText: "Greater than",
    operatorGreaterOrEqualText: "Greater than or equal",
    operatorContainsText: "Contains",
    operatorDoesNotContainText: "Does not contain",
    operatorEqualsText: "Equals",
    operatorDoesNotEqualText: "Does not equal",
    operatorStartsWithText: "Starts with",
    operatorDoesNotStartWithText: "Does not start with",

    editTokenHeader: "Edit filter",
    propertyText: "Property",
    operatorText: "Operator",
    valueText: "Value",
    cancelActionText: "Cancel",
    applyActionText: "Apply",
    allPropertiesLabel: "All properties",

    tokenLimitShowMore: "Show more",
    tokenLimitShowFewer: "Show fewer",
    clearFiltersText: "Clear filters",
    removeTokenButtonAriaLabel: (token) =>
      `Remove token ${token.propertyKey} ${token.operator} ${token.value}`,
    enteredTextLabel: (text) => `Use: "${text}"`,
  };

export function createLabelFunction(columnName: string) {
  return ({ sorted, descending }: TableProps.LabelData) => {
    const sortState = sorted
      ? `sorted ${descending ? "descending" : "ascending"}`
      : "not sorted";
    return `${columnName}, ${sortState}.`;
  };
}

export function getMatchesCountText(count: any) {
  return count === 1 ? "1 match" : `${count} matches`;
}

export function taskComparator(a: Task, b: Task) {
  if (
    (a.order !== undefined && a.order !== -1) ||
    (b.order !== undefined && b.order !== -1)
  ) {
    //Sort by order
    //Task comes second if order does not exist
    const orderA =
      a.order === undefined || a.order === -1 || a.order === null
        ? Number.MAX_SAFE_INTEGER
        : a.order;
    const orderB =
      b.order === undefined || b.order === -1 || b.order === null
        ? Number.MAX_SAFE_INTEGER
        : b.order;
    return orderA - orderB;
  }

  if (a.dueDate || b.dueDate) {
    //Sort by dueDate
    //Task comes second if dueDate does not exist
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    const dueDateA = a.dueDate ?? Number.MAX_SAFE_INTEGER;
    const dueDateB = b.dueDate ?? Number.MAX_SAFE_INTEGER;
    if (dueDateA !== dueDateB) {
      return dueDateB - dueDateA;
    }
  }

  //Sort by createdAt
  const createdAtA = a.createdAt ?? 0;
  const createdAtB = b.createdAt ?? 0;

  if (createdAtA !== createdAtB) {
    return createdAtB - createdAtA;
  }

  //Sort by taskName
  return (a.taskName ?? "").localeCompare(b.taskName ?? "");
}

export const calculateDueDays = (dueDate: number) => {
  return calculateDueDaysWithReference(dueDate, moment());
};

export const calculateDueDaysWithReference = (
  dueDate: number,
  referenceDate: moment.Moment = moment(),
) => {
  const today = moment.utc(referenceDate.format()).startOf("day");
  const dueDateMoment = moment.utc(dueDate * 1000).startOf("day");
  const diffDays = dueDateMoment.diff(today, "days");

  if (diffDays < 0) {
    return {
      text: `overdue ${Math.abs(diffDays)} ${
        Math.abs(diffDays) === 1 ? "day" : "days"
      }`,
      isOverdue: true,
    };
  }
  if (diffDays === 0) {
    return {
      text: "due today",
      isOverdue: false,
    };
  }
  return {
    text: `due in ${diffDays} ${diffDays === 1 ? "day" : "days"}`,
    isOverdue: false,
  };
};

export const fieldFormatter = (value: any, type?: string) => {
  if (!value || value === 0 || value === "") {
    return "-";
  }
  if (type === "dueIn") {
    return moment.utc(toEndOfDayTimestamp(toDateString(value))).fromNow();
  }
  return !type ? value : formatDateValue(Number(value));
};

export const dateFieldComparator = (field: string) => {
  return (a: any, b: any) => {
    const aDate = a?.[field];
    const bDate = b?.[field];

    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;

    return Number(bDate) - Number(aDate);
  };
};

export const paginationLabels: PaginationProps.Labels = {
  nextPageLabel: "Next page",
  pageLabel: (pageNumber) => `Go to page ${pageNumber}`,
  previousPageLabel: "Previous page",
};

export const pageSizePreference = {
  title: "Page size",
  options: [
    { value: 10, label: "10 items" },
    { value: 25, label: "25 items" },
    { value: 100, label: "100 items" },
    { value: 200, label: "200 items" },
  ],
};

export const tablePreferenceLabels = {
  wrapLinesPreference: {
    label: "Wrap lines",
    description: "Select to see all the text and wrap the lines",
  },
  stripedRowsPreference: {
    label: "Striped rows",
    description: "Select to add alternating shaded rows",
  },
  contentDensityPreference: {
    label: "Compact mode",
    description: "Select to display compact mode",
  },
  stickyColumnsPreference: {
    firstColumns: {
      title: "First column(s)",
      description:
        "Keep the first column(s) visible while horizontally scrolling table content.",
      options: [
        { label: "None", value: 0 },
        { label: "First column", value: 1 },
        { label: "First two columns", value: 2 },
      ],
    },
    lastColumns: {
      title: "Stick last visible column",
      description:
        "Keep the last column visible when tables are wider than the viewport.",
      options: [
        { label: "None", value: 0 },
        { label: "Last column", value: 1 },
        { label: "Last two columns", value: 2 },
      ],
    },
  },
};

/**
 * Creates a numeric filtering property
 */
export const createNumericFilteringProperty = (
  propertyLabel: string,
  key: string,
) => {
  return {
    propertyLabel: propertyLabel,
    key: key,
    groupValuesLabel: `${propertyLabel} values`,
    operators: ["=", "!=", "<", "<=", ">", ">="].map((operator) => ({
      operator,
      match: (itemValue: string, filterValue: string) => {
        const numValue = Number.parseFloat(filterValue);
        if (Number.isNaN(numValue)) return false;
        const actualValue = Number(itemValue);
        switch (operator) {
          case "=":
            return actualValue === numValue;
          case "!=":
            return actualValue !== numValue;
          case "<":
            return actualValue < numValue;
          case "<=":
            return actualValue <= numValue;
          case ">":
            return actualValue > numValue;
          case ">=":
            return actualValue >= numValue;
          default:
            return false;
        }
      },
    })),
  };
};

/**
 * Table descriptions for guests are different from teams.
 * This will return the correct extension for the translation.json file.
 */
export function getTableDescriptionExtension(teamName: string) {
  return teamName === "guest" ? "guest_description" : "description";
}
